import { FC, useState, useEffect, type CSSProperties } from 'react'
import { CargoOperation } from '../core/_models'
import { approveCargoOperation, rejectCargoOperation } from '../core/_requests'
import { toast } from 'react-toastify'
import { getApiErrorMessage } from '../../../shared/utils/apiErrorMessage'

interface ReviewCargoModalProps {
  isOpen: boolean
  onClose: () => void
  cargo: CargoOperation | null
  initialAction?: 'APPROVE' | 'REJECT'
  onSuccess: () => void
}

const WRAP_STYLE: CSSProperties = {
  background: 'rgba(0,0,0,0.5)',
  position: 'fixed',
  inset: 0,
  zIndex: 1060,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}

const ReviewCargoModal: FC<ReviewCargoModalProps> = ({
  isOpen,
  onClose,
  cargo,
  initialAction = 'APPROVE',
  onSuccess,
}) => {
  const [action, setAction] = useState<'APPROVE' | 'REJECT'>(initialAction)
  const [comments, setComments] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (isOpen) {
      setAction(initialAction)
      setComments('')
      setError('')
      setLoading(false)
    }
  }, [isOpen, initialAction])

  if (!isOpen || !cargo) return null

  const handleAction = async () => {
    if (!cargo.id) return

    if (action === 'REJECT' && !comments.trim()) {
      setError('Please provide comments explaining the reason for rejection.')
      return
    }

    setLoading(true)
    setError('')
    try {
      if (action === 'APPROVE') {
        await approveCargoOperation(cargo.id, comments.trim() || undefined)
        toast.success('Cargo report approved and locked successfully.', {
          position: 'top-center',
        })
      } else {
        await rejectCargoOperation(cargo.id, comments.trim())
        toast.info('Cargo report rejected and returned to crew for revisions.', {
          position: 'top-center',
        })
      }
      onSuccess()
      onClose()
    } catch (err: any) {
      const msg = getApiErrorMessage(err, `Failed to ${action.toLowerCase()} cargo report`)
      setError(msg)
      toast.error(msg, { position: 'top-center' })
    } finally {
      setLoading(false)
    }
  }

  const isApprove = action === 'APPROVE'

  return (
    <div style={WRAP_STYLE} tabIndex={-1}>
      <div
        className='bg-white text-dark rounded shadow-lg d-flex flex-column'
        style={{
          width: '90vw',
          maxWidth: '560px',
        }}
      >
        <div
          className={`d-flex align-items-center justify-content-between border-bottom px-5 py-4 ${
            isApprove ? 'bg-light-success' : 'bg-light-danger'
          }`}
          style={{ borderTopLeftRadius: '0.475rem', borderTopRightRadius: '0.475rem' }}
        >
          <div className='d-flex align-items-center gap-2'>
            <i
              className={`bi ${
                isApprove ? 'bi-check-circle-fill text-success' : 'bi-x-circle-fill text-danger'
              } fs-2`}
            ></i>
            <h5 className='modal-title text-dark mb-0'>
              {isApprove ? 'Approve Cargo Report' : 'Reject Cargo Report'}
            </h5>
          </div>
          <button
            type='button'
            className='btn-close'
            onClick={onClose}
            disabled={loading}
          ></button>
        </div>

        <div className='p-5'>
          {/* Action toggle selector */}
          <div className='d-flex gap-2 mb-4'>
            <button
              type='button'
              className={`btn btn-sm flex-fill ${
                isApprove ? 'btn-success' : 'btn-light'
              }`}
              onClick={() => {
                setAction('APPROVE')
                setError('')
              }}
              disabled={loading}
            >
              <i className='bi bi-check-lg me-1'></i> Approve
            </button>
            <button
              type='button'
              className={`btn btn-sm flex-fill ${
                !isApprove ? 'btn-danger' : 'btn-light'
              }`}
              onClick={() => {
                setAction('REJECT')
                setError('')
              }}
              disabled={loading}
            >
              <i className='bi bi-x-lg me-1'></i> Reject
            </button>
          </div>

          {/* Details summary */}
          <div className='bg-light rounded p-3 mb-4 fs-7'>
            <div className='row g-2'>
              <div className='col-6'>
                <span className='text-muted'>Vessel: </span>
                <span className='fw-bold text-dark'>{cargo.vesselName || '-'}</span>
              </div>
              <div className='col-6'>
                <span className='text-muted'>Voyage: </span>
                <span className='fw-bold text-dark'>{cargo.voyageNumber || '-'}</span>
              </div>
              <div className='col-6'>
                <span className='text-muted'>Breakup: </span>
                <span className='fw-bold text-dark'>{cargo.breakupType}</span>
              </div>
              <div className='col-6'>
                <span className='text-muted'>Total Qty: </span>
                <span className='fw-bold text-dark'>
                  {cargo.totalCargoQtyMt != null ? `${cargo.totalCargoQtyMt} MT` : '-'}
                </span>
              </div>
            </div>
          </div>

          {/* Explanation message */}
          {isApprove ? (
            <div className='alert alert-success d-flex align-items-center py-2 px-3 mb-3 fs-7'>
              <i className='bi bi-shield-lock-fill text-success fs-4 me-2'></i>
              <span>
                Approving this report will <strong>lock</strong> it permanently. No further
                edits or deletions will be allowed.
              </span>
            </div>
          ) : (
            <div className='alert alert-warning d-flex align-items-center py-2 px-3 mb-3 fs-7'>
              <i className='bi bi-info-circle-fill text-warning fs-4 me-2'></i>
              <span>
                Rejecting this report will return it to the crew with your comments so they can
                revise and re-submit it.
              </span>
            </div>
          )}

          {/* Comments input */}
          <div className='mb-3'>
            <label className='form-label fw-semibold fs-7 mb-1'>
              {isApprove ? 'Approval Comments (Optional)' : 'Reason for Rejection *'}
            </label>
            <textarea
              className={`form-control ${error ? 'is-invalid' : ''}`}
              rows={3}
              placeholder={
                isApprove
                  ? 'Enter approval remarks, endorsements, or notes...'
                  : 'Specify what requires correction before approval...'
              }
              value={comments}
              onChange={(e) => {
                setComments(e.target.value)
                if (error) setError('')
              }}
              disabled={loading}
            ></textarea>
            {error && <div className='invalid-feedback d-block'>{error}</div>}
          </div>
        </div>

        <div className='border-top d-flex justify-content-end gap-2 px-5 py-3 bg-light' style={{ borderBottomLeftRadius: '0.475rem', borderBottomRightRadius: '0.475rem' }}>
          <button
            type='button'
            className='btn btn-light btn-sm'
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type='button'
            className={`btn btn-sm ${isApprove ? 'btn-success' : 'btn-danger'}`}
            onClick={handleAction}
            disabled={loading}
          >
            {loading ? (
              <>
                <span className='spinner-border spinner-border-sm me-1'></span>
                Processing...
              </>
            ) : isApprove ? (
              <>
                <i className='bi bi-check2-circle me-1'></i> Confirm Approve & Lock
              </>
            ) : (
              <>
                <i className='bi bi-arrow-counterclockwise me-1'></i> Confirm Reject & Return
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

export default ReviewCargoModal
